<template>
  <div class="business-calendar-page">
    <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-5">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">영업일 관리</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">지점별 영업일, 휴무일, 방문수령 예약 기준을 관리합니다.</p>
      </div>
      <div class="d-flex ga-2">
        <v-select
          v-model="selectedYear"
          :items="yearOptions"
          density="compact"
          hide-details
          label="연도"
          variant="outlined"
          style="width: 130px"
          @update:model-value="loadCalendar"
        />
        <v-select
          v-model="selectedBranchCode"
          :items="branchOptions"
          density="compact"
          hide-details
          label="지점"
          variant="outlined"
          style="width: 240px"
          @update:model-value="loadCalendar"
        />
      </div>
    </div>

    <v-alert v-if="message" class="mb-4" :type="messageType" variant="tonal" density="comfortable">
      {{ message }}
    </v-alert>

    <v-row>
      <v-col cols="12" lg="7">
        <v-card class="admin-card mb-4">
          <v-card-title class="text-subtitle-1 font-weight-bold">주간 영업 규칙</v-card-title>
          <v-card-text>
            <div class="weekly-grid">
              <div v-for="day in weeklyRules" :key="day.dayOfWeek" class="weekly-item">
                <div>
                  <div class="font-weight-bold">{{ day.label }}</div>
                  <div class="text-caption text-medium-emphasis">{{ day.businessYn === 'Y' ? '영업일' : '휴무일' }}</div>
                </div>
                <v-switch
                  v-model="day.businessYn"
                  true-value="Y"
                  false-value="N"
                  color="primary"
                  hide-details
                  inset
                />
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn color="primary" variant="flat" @click="saveWeeklyRules">주간 규칙 저장</v-btn>
          </v-card-actions>
        </v-card>

        <v-card class="admin-card">
          <v-card-title class="d-flex align-center justify-space-between">
            <span class="text-subtitle-1 font-weight-bold">특정일 예외</span>
            <v-btn color="primary" variant="flat" size="small" prepend-icon="mdi-calendar-plus" @click="openOverrideDialog()">예외 등록</v-btn>
          </v-card-title>
          <v-table class="admin-table">
            <thead>
              <tr>
                <th>일자</th>
                <th>구분</th>
                <th>제목</th>
                <th>유형</th>
                <th class="text-right">관리</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in dateOverrides" :key="item.dateOverrideCode">
                <td>{{ item.targetDate }}</td>
                <td><v-chip size="small" :color="item.businessYn === 'Y' ? 'success' : 'error'" variant="tonal">{{ item.businessYn === 'Y' ? '영업' : '휴무' }}</v-chip></td>
                <td>{{ item.title || '-' }}</td>
                <td>{{ overrideTypeLabel(item.overrideType) }}</td>
                <td class="text-right">
                  <v-btn icon="mdi-pencil" variant="text" size="small" @click="openOverrideDialog(item)" />
                  <v-btn icon="mdi-delete-outline" variant="text" size="small" color="error" @click="removeOverride(item)" />
                </td>
              </tr>
              <tr v-if="dateOverrides.length === 0">
                <td colspan="5" class="text-center text-medium-emphasis py-8">등록된 특정일 예외가 없습니다.</td>
              </tr>
            </tbody>
          </v-table>
        </v-card>
      </v-col>

      <v-col cols="12" lg="5">
        <v-card v-if="isHq" class="admin-card mb-4">
          <v-card-title class="text-subtitle-1 font-weight-bold">공통 공휴일</v-card-title>
          <v-card-text>
            <div class="d-flex ga-2 mb-3">
              <v-text-field v-model="holidayForm.holidayDate" type="date" label="일자" variant="outlined" density="compact" hide-details />
              <v-text-field v-model="holidayForm.holidayNm" label="공휴일명" variant="outlined" density="compact" hide-details />
              <v-btn color="primary" variant="flat" @click="saveHoliday">등록</v-btn>
            </div>
            <v-table density="compact" class="admin-table">
              <tbody>
                <tr v-for="holiday in publicHolidays" :key="holiday.publicHolidayCode">
                  <td>{{ holiday.holidayDate }}</td>
                  <td>{{ holiday.holidayNm }}</td>
                  <td class="text-right"><v-btn icon="mdi-delete-outline" variant="text" size="small" @click="removeHoliday(holiday)" /></td>
                </tr>
                <tr v-if="publicHolidays.length === 0">
                  <td colspan="3" class="text-center text-medium-emphasis py-6">등록된 공휴일이 없습니다.</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>

        <v-card v-if="isHq" class="admin-card mb-4">
          <v-card-title class="text-subtitle-1 font-weight-bold">몰 방문수령 정책</v-card-title>
          <v-card-text>
            <v-text-field
              v-model.number="pickupPolicy.branchChangeLeadBusinessDays"
              type="number"
              min="0"
              max="30"
              label="지점 변경 시 준비 영업일"
              variant="outlined"
              density="compact"
            />
            <v-textarea v-model="pickupPolicy.memo" label="메모" variant="outlined" density="compact" rows="2" />
            <v-btn color="primary" variant="flat" @click="savePickupPolicy">정책 저장</v-btn>
          </v-card-text>
        </v-card>

        <v-card v-if="isHq" class="admin-card">
          <v-card-title class="text-subtitle-1 font-weight-bold">영업일 복사</v-card-title>
          <v-card-text>
            <p class="text-caption text-medium-emphasis mb-3">골드나라 지점 간 영업일 설정을 복사합니다.</p>
            <v-select v-model="copyForm.sourceBranchCode" :items="branchOptions" label="원본 지점" variant="outlined" density="compact" />
            <div class="d-flex ga-2">
              <v-text-field v-model.number="copyForm.sourceCalendarYear" type="number" label="원본 연도" variant="outlined" density="compact" />
              <v-text-field v-model.number="copyForm.targetCalendarYear" type="number" label="대상 연도" variant="outlined" density="compact" />
            </div>
            <div class="d-flex flex-wrap ga-3 mb-3">
              <v-checkbox v-model="copyForm.copyWeeklyRuleYn" true-value="Y" false-value="N" label="주간 규칙" hide-details />
              <v-checkbox v-model="copyForm.copyOverrideYn" true-value="Y" false-value="N" label="특정일 예외" hide-details />
              <v-checkbox v-model="copyForm.copyPublicHolidayYn" true-value="Y" false-value="N" label="공통 공휴일" hide-details />
              <v-checkbox v-model="copyForm.replaceTargetYn" true-value="Y" false-value="N" label="대상 기존값 교체" hide-details />
            </div>
            <v-btn color="primary" variant="flat" @click="copyCalendar">선택 지점으로 복사</v-btn>
            <v-divider class="my-4" />
            <div class="text-caption text-medium-emphasis mb-2">최근 복사 이력</div>
            <div v-for="history in copyHistories" :key="history.copyHistoryCode" class="copy-history-row">
              <b>{{ history.resultStatus }}</b>
              <span>{{ history.sourceBranchName || history.sourceBranchCode }} → {{ history.targetBranchName || history.targetBranchCode }}</span>
              <small>{{ history.createdDt }}</small>
            </div>
          </v-card-text>
        </v-card>

        <v-card v-if="isHq" class="admin-card mt-4">
          <v-card-title class="text-subtitle-1 font-weight-bold">아우라 영업일 연동</v-card-title>
          <v-card-text>
            <p class="text-caption text-medium-emphasis mb-3">선택된 골드나라 지점과 아우라 지점 간 영업일 설정을 API로 복사합니다.</p>
            <v-text-field
              v-model="auraCopyForm.remoteBranchCode"
              label="아우라 지점코드"
              placeholder="아우라의 대상 지점코드"
              variant="outlined"
              density="compact"
            />
            <div class="d-flex ga-2">
              <v-text-field v-model.number="auraCopyForm.sourceCalendarYear" type="number" label="원본 연도" variant="outlined" density="compact" />
              <v-text-field v-model.number="auraCopyForm.targetCalendarYear" type="number" label="대상 연도" variant="outlined" density="compact" />
            </div>
            <div class="d-flex flex-wrap ga-3 mb-3">
              <v-checkbox v-model="auraCopyForm.copyWeeklyRuleYn" true-value="Y" false-value="N" label="주간 규칙" hide-details />
              <v-checkbox v-model="auraCopyForm.copyOverrideYn" true-value="Y" false-value="N" label="특정일 예외" hide-details />
              <v-checkbox v-model="auraCopyForm.copyPublicHolidayYn" true-value="Y" false-value="N" label="공통 공휴일" hide-details />
              <v-checkbox v-model="auraCopyForm.replaceTargetYn" true-value="Y" false-value="N" label="대상 기존값 교체" hide-details />
            </div>
            <div class="d-flex flex-wrap ga-2">
              <v-btn color="primary" variant="flat" prepend-icon="mdi-download" @click="copyFromAura">아우라에서 복사</v-btn>
              <v-btn color="secondary" variant="tonal" prepend-icon="mdi-upload" @click="copyToAura">아우라로 복사</v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="overrideDialog" max-width="520">
      <v-card class="admin-dialog">
        <v-card-title class="font-weight-bold">{{ editingOverride?.dateOverrideCode ? '특정일 예외 수정' : '특정일 예외 등록' }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="overrideForm.targetDate" type="date" label="대상일자" variant="outlined" />
          <v-select v-model="overrideForm.businessYn" :items="businessYnOptions" label="영업여부" variant="outlined" />
          <v-select v-model="overrideForm.overrideType" :items="overrideTypeOptions" label="예외유형" variant="outlined" />
          <v-text-field v-model="overrideForm.title" label="제목" variant="outlined" />
          <v-textarea v-model="overrideForm.memo" label="메모" rows="3" variant="outlined" />
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="overrideDialog = false">취소</v-btn>
          <v-btn color="primary" variant="flat" @click="saveOverride">저장</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getBranchList } from '@/api/branch.js'
import {
  copyBusinessCalendar,
  createDateOverride,
  createPublicHoliday,
  deleteDateOverride,
  deletePublicHoliday,
  getBranchCalendar,
  updateDateOverride,
  updatePickupPolicy,
  updateWeeklyRules,
} from '@/api/businessCalendar.js'
import { getAdminInfo } from '@/utils/token.js'

const dayLabels = ['월', '화', '수', '목', '금', '토', '일']

export default {
  name: 'BusinessCalendarPage',
  data() {
    const year = new Date().getFullYear()
    return {
      adminInfo: getAdminInfo(),
      selectedYear: year,
      selectedBranchCode: '',
      branches: [],
      weeklyRules: dayLabels.map((label, idx) => ({ dayOfWeek: idx + 1, label, businessYn: idx < 5 ? 'Y' : 'N' })),
      dateOverrides: [],
      publicHolidays: [],
      pickupPolicy: { branchChangeLeadBusinessDays: 4, memo: '' },
      copyHistories: [],
      holidayForm: { holidayDate: '', holidayNm: '' },
      copyForm: {
        sourceBranchCode: '',
        sourceCalendarYear: year,
        targetCalendarYear: year,
        copyWeeklyRuleYn: 'Y',
        copyOverrideYn: 'Y',
        copyPublicHolidayYn: 'Y',
        replaceTargetYn: 'Y',
      },
      auraCopyForm: {
        remoteBranchCode: '',
        sourceCalendarYear: year,
        targetCalendarYear: year,
        copyWeeklyRuleYn: 'Y',
        copyOverrideYn: 'Y',
        copyPublicHolidayYn: 'Y',
        replaceTargetYn: 'Y',
      },
      overrideDialog: false,
      editingOverride: null,
      overrideForm: { targetDate: '', businessYn: 'N', overrideType: 'BRANCH', title: '', memo: '' },
      message: '',
      messageType: 'success',
    }
  },
  computed: {
    isHq() {
      return this.adminInfo.adminType === 'HQ'
    },
    yearOptions() {
      const current = new Date().getFullYear()
      return Array.from({ length: 6 }, (_, idx) => current - 1 + idx)
    },
    branchOptions() {
      return this.branches.map(branch => ({ title: branch.branchName, value: branch.branchCode }))
    },
    businessYnOptions() {
      return [{ title: '영업일', value: 'Y' }, { title: '휴무일', value: 'N' }]
    },
    overrideTypeOptions() {
      return [
        { title: '공통 공휴일', value: 'PUBLIC_HOLIDAY' },
        { title: '특별 영업', value: 'SPECIAL_OPEN' },
        { title: '특별 휴무', value: 'SPECIAL_CLOSED' },
        { title: '지점 설정', value: 'BRANCH' },
        { title: '본사 설정', value: 'HEAD' },
        { title: '기타', value: 'ETC' },
      ]
    },
  },
  async mounted() {
    await this.loadBranches()
    await this.loadCalendar()
  },
  methods: {
    async loadBranches() {
      const { data } = await getBranchList({ page: 1, size: 500 })
      this.branches = data?.data?.items || []
      if (!this.selectedBranchCode) {
        this.selectedBranchCode = this.isHq ? this.branches[0]?.branchCode : this.adminInfo.branchCode
      }
      if (!this.copyForm.sourceBranchCode) this.copyForm.sourceBranchCode = this.selectedBranchCode
    },
    async loadCalendar() {
      if (!this.selectedBranchCode) return
      const { data } = await getBranchCalendar(this.selectedBranchCode, { year: this.selectedYear })
      const rules = data.weeklyRules || []
      this.weeklyRules = dayLabels.map((label, idx) => {
        const dayOfWeek = idx + 1
        const saved = rules.find(rule => Number(rule.dayOfWeek) === dayOfWeek)
        return { dayOfWeek, label, businessYn: saved?.businessYn || (idx < 5 ? 'Y' : 'N'), memo: saved?.memo || '' }
      })
      this.dateOverrides = data.dateOverrides || []
      this.publicHolidays = data.publicHolidays || []
      this.pickupPolicy = data.pickupPolicy || this.pickupPolicy
      this.copyHistories = data.copyHistories || []
    },
    async saveWeeklyRules() {
      await updateWeeklyRules(this.selectedBranchCode, this.selectedYear, this.weeklyRules)
      this.toast('주간 영업 규칙을 저장했습니다.')
      await this.loadCalendar()
    },
    openOverrideDialog(item = null) {
      this.editingOverride = item
      this.overrideForm = item
        ? { ...item }
        : { targetDate: '', businessYn: 'N', overrideType: 'BRANCH', title: '', memo: '' }
      this.overrideDialog = true
    },
    async saveOverride() {
      if (this.editingOverride?.dateOverrideCode) {
        await updateDateOverride(this.selectedBranchCode, this.editingOverride.dateOverrideCode, this.overrideForm)
      } else {
        await createDateOverride(this.selectedBranchCode, this.overrideForm)
      }
      this.overrideDialog = false
      this.toast('특정일 예외를 저장했습니다.')
      await this.loadCalendar()
    },
    async removeOverride(item) {
      if (!confirm(`${item.targetDate} 예외를 삭제할까요?`)) return
      await deleteDateOverride(this.selectedBranchCode, item.dateOverrideCode)
      this.toast('특정일 예외를 삭제했습니다.')
      await this.loadCalendar()
    },
    async saveHoliday() {
      await createPublicHoliday({ ...this.holidayForm, holidayType: 'NATIONAL', displayYn: 'Y' })
      this.holidayForm = { holidayDate: '', holidayNm: '' }
      this.toast('공휴일을 등록했습니다.')
      await this.loadCalendar()
    },
    async removeHoliday(item) {
      if (!confirm(`${item.holidayNm} 공휴일을 삭제할까요?`)) return
      await deletePublicHoliday(item.publicHolidayCode)
      this.toast('공휴일을 삭제했습니다.')
      await this.loadCalendar()
    },
    async savePickupPolicy() {
      await updatePickupPolicy(this.pickupPolicy)
      this.toast('방문수령 정책을 저장했습니다.')
      await this.loadCalendar()
    },
    async copyCalendar() {
      await copyBusinessCalendar({
        ...this.copyForm,
        targetBranchCode: this.selectedBranchCode,
        copyDirection: 'INTERNAL',
        sourceSystem: 'GOLDNARA',
        targetSystem: 'GOLDNARA',
      })
      this.toast('영업일 설정을 복사했습니다.')
      await this.loadCalendar()
    },
    async copyFromAura() {
      if (!this.auraCopyForm.remoteBranchCode) {
        this.toast('아우라 지점코드를 입력해 주세요.', 'error')
        return
      }
      await copyBusinessCalendar({
        sourceBranchCode: this.auraCopyForm.remoteBranchCode,
        targetBranchCode: this.selectedBranchCode,
        sourceCalendarYear: this.auraCopyForm.sourceCalendarYear,
        targetCalendarYear: this.auraCopyForm.targetCalendarYear,
        copyWeeklyRuleYn: this.auraCopyForm.copyWeeklyRuleYn,
        copyOverrideYn: this.auraCopyForm.copyOverrideYn,
        copyPublicHolidayYn: this.auraCopyForm.copyPublicHolidayYn,
        replaceTargetYn: this.auraCopyForm.replaceTargetYn,
        copyDirection: 'AURA_TO_GOLDNARA',
        sourceSystem: 'AURA',
        targetSystem: 'GOLDNARA',
      })
      this.toast('아우라 영업일을 골드나라로 복사했습니다.')
      await this.loadCalendar()
    },
    async copyToAura() {
      if (!this.auraCopyForm.remoteBranchCode) {
        this.toast('아우라 지점코드를 입력해 주세요.', 'error')
        return
      }
      await copyBusinessCalendar({
        sourceBranchCode: this.selectedBranchCode,
        targetBranchCode: this.auraCopyForm.remoteBranchCode,
        sourceCalendarYear: this.auraCopyForm.sourceCalendarYear,
        targetCalendarYear: this.auraCopyForm.targetCalendarYear,
        copyWeeklyRuleYn: this.auraCopyForm.copyWeeklyRuleYn,
        copyOverrideYn: this.auraCopyForm.copyOverrideYn,
        copyPublicHolidayYn: this.auraCopyForm.copyPublicHolidayYn,
        replaceTargetYn: this.auraCopyForm.replaceTargetYn,
        copyDirection: 'GOLDNARA_TO_AURA',
        sourceSystem: 'GOLDNARA',
        targetSystem: 'AURA',
      })
      this.toast('골드나라 영업일을 아우라로 복사했습니다.')
      await this.loadCalendar()
    },
    overrideTypeLabel(type) {
      return this.overrideTypeOptions.find(item => item.value === type)?.title || type
    },
    toast(text, type = 'success') {
      this.message = text
      this.messageType = type
      window.setTimeout(() => {
        this.message = ''
      }, 3000)
    },
  },
}
</script>

<style scoped>
.admin-card,
.admin-dialog {
  background: #1c212b;
  border: 1px solid #384354;
  color: #e9edf5;
}

.weekly-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
}

.weekly-item {
  align-items: center;
  background: #131a25;
  border: 1px solid #344054;
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  padding: 12px;
}

.admin-table {
  background: transparent;
  color: #e9edf5;
}

.copy-history-row {
  align-items: center;
  border: 1px solid #344054;
  border-radius: 8px;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  margin-top: 8px;
  padding: 10px 12px;
}
</style>
